import { useRef, useState } from 'react'
import styled from 'styled-components'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { RAG } from 'features/dashboard/charts/scoreColour'
import DatePopover from './DatePopover'
import { dueInfo, formatDay } from './remedialDates'
import { BORDER, INK, MUTED } from './ui'

const EDITABLE_STAGES = ['needs_decision', 'awaiting_review']

const DueCell = ({ action, onChange, variant = 'field' }) => {
  const row = variant === 'row'
  const btnRef = useRef(null)
  const [open, setOpen] = useState(false)
  const info = dueInfo(action)
  const editable = !!action.permissions?.canUpdate && !info.followsTask && EDITABLE_STAGES.includes(action.stage)
  const tone = info.level ? RAG[info.level] : null
  const dateText = info.date ? formatDay(info.date) : 'No due date'

  const content = (
    <Stack $row={row} data-variant={variant} title={row && info.followsTask ? 'Set by its task deadline' : undefined}>
      <DateText $hideMobile={row && !!info.label}>{dateText}</DateText>
      {info.label && (
        <Tag style={tone ? { color: tone.fg, background: tone.bg } : undefined} $plain={!tone} $row={row}>{info.label}</Tag>
      )}
      {info.followsTask && !row && <Note>Set by its task deadline</Note>}
    </Stack>
  )

  if (!editable) return row ? <RowStatic>{content}</RowStatic> : content

  return (
    <>
      <EditButton
        ref={btnRef}
        type='button'
        $row={row}
        aria-label={`Due ${dateText}. Change due date`}
        onClick={e => { e.stopPropagation(); setOpen(true) }}
      >
        {content}
        <EditHint style={{ fontSize: 16, color: MUTED }} aria-hidden='true' $row={row} />
      </EditButton>
      <DatePopover
        anchorEl={btnRef.current}
        open={open}
        onClose={() => setOpen(false)}
        value={action.dueDate || ''}
        title='Change due date'
        helper='The date this remedial must be put right by. It counts as overdue from the day after.'
        saveLabel='Save date'
        onSave={date => {
          setOpen(false)
          if (date !== action.dueDate) onChange(action, date)
        }}
      />
    </>
  )
}

export default DueCell

const Stack = styled.span`
  display: inline-flex;
  flex-direction: ${({ $row }) => ($row ? 'row' : 'column')};
  flex-wrap: ${({ $row }) => ($row ? 'wrap' : 'nowrap')};
  align-items: ${({ $row }) => ($row ? 'center' : 'flex-start')};
  gap: ${({ $row }) => ($row ? '2px 6px' : '4px')};
  min-width: 0;
  max-width: 100%;
`

const DateText = styled.span`
  font-size: 15px;
  [data-variant='row'] & { font-size: 14px; }
  @media (max-width: 768px) { ${({ $hideMobile }) => ($hideMobile ? 'display: none;' : '')} }
  color: ${INK};
  white-space: nowrap;
`

const Tag = styled.span`
  display: inline-flex;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ $row }) => ($row ? '12.5px' : '14px')};
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $plain }) => ($plain ? MUTED : 'inherit')};
  background: ${({ $plain }) => ($plain ? 'transparent' : 'inherit')};
  padding-left: ${({ $plain }) => ($plain ? '0' : '8px')};
`

const Note = styled.span`
  font-size: 14px;
  color: ${MUTED};
`

const EditHint = styled(EditOutlinedIcon)`
  flex-shrink: 0;
  ${({ $row }) => ($row ? 'opacity: 0; transition: opacity 0.12s ease;' : '')}
  @media (hover: none) { opacity: 1; }
`

const RowStatic = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 8px;
`

const EditButton = styled.button`
  display: inline-flex;
  align-items: ${({ $row }) => ($row ? 'center' : 'flex-start')};
  gap: 8px;
  min-height: 40px;
  max-width: 100%;
  padding: ${({ $row }) => ($row ? '4px 8px' : '6px 8px')};
  border: 1px solid ${({ $row }) => ($row ? 'transparent' : BORDER)};
  border-radius: 8px;
  background: ${({ $row }) => ($row ? 'transparent' : '#fff')};
  color: ${INK};
  font: inherit;
  font-size: ${({ $row }) => ($row ? '14px' : '15px')};
  text-align: left;
  cursor: pointer;
  &:hover { border-color: #a1a1aa; background: #fff; }
  &:hover ${EditHint}, &:focus-visible ${EditHint}, tr:hover & ${EditHint} { opacity: 1; }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; }
`
